import clsx from 'clsx'
import { atom } from 'nanostores'
import { type ButtonHTMLAttributes, type HTMLAttributes, useId } from 'react'
import { appDay } from 'shared/day'
import { contextActions, typo } from 'shared/styles'
import { StackIcon } from 'shared/ui'

import type { Message } from '../../model/types'

import { api } from '../../api'
import { useConversationalist, useCurrentId } from '../../hooks'
import { $confirmStore, ConfirmDialogMode } from '../ConfirmDialog'
import { EmojiSelect } from '../Emoji/Select'
import { $editMessage } from '../MessageForm/form'
import css from './MessageActions.module.scss'

const isFresh = (day?: string) =>
	Boolean(day) && appDay().diff(day, 'hour') <= 1

export const $reply = atom<Message | null>(null)

export function MessageActions({
	className,
	isText = false,
	message,
	...props
}: HTMLAttributes<HTMLDivElement> & {
	isText?: boolean
	message: Message
}) {
	const conversationalist = useConversationalist()

	const isInterlocutorsMessage =
		message?.from?.entityId === conversationalist?.entityId

	const showContextMenu =
		!isInterlocutorsMessage && isFresh(message?.created_at)

	return (
		<div className={clsx(css.menu, typo.buttonSmB, className)} {...props}>
			{message && <EmojiSelect {...message} />}
			{isText && (
				<button
					aria-haspopup='true'
					aria-label='Open menu'
					className='li-⚪'
					onClick={() => $reply.set(message)}
					type='button'
				>
					<StackIcon icon='reply' stack='linkedin' width={16} />
				</button>
			)}
			{showContextMenu && <SubActions {...message} />}
		</div>
	)
}

function SubActions({ message_id: id = '', text = '' }: Message) {
	const subActions = useId()
	const conversation = useCurrentId()
	const [remove, removeProps] = api.useDeleteMessageMutation()

	const actionProps = {
		className: contextActions.action,
		popovertarget: subActions,
		popovertargetaction: 'hide',
		type: 'button'
	} as ButtonHTMLAttributes<HTMLButtonElement>

	const isRemoving =
		removeProps.isLoading && removeProps.originalArgs?.id === id

	return (
		<>
			<button
				aria-haspopup='true'
				aria-label='Open menu'
				className={`li-⚪ ${css.subTrigger}`}
				// @ts-ignore
				popovertarget={subActions}
				popovertargetaction='toggle'
				type='button'
			>
				<StackIcon icon='dots' stack='linkedin' />
			</button>
			<div
				className={clsx(css.subMenu, contextActions.menu, typo.buttonSmB)}
				id={subActions}
				// @ts-ignore Recheck in React 19
				popover='auto'
			>
				<button
					disabled={isRemoving}
					onClick={() => {
						$confirmStore.set({
							confirm: () =>
								remove({
									conversation,
									id
								}),
							mode: ConfirmDialogMode.DeleteMessage
						})
					}}
					{...actionProps}
				>
					Delete
				</button>

				{Boolean(text) && (
					<button
						onClick={() =>
							$editMessage.set({
								id,
								text
							})
						}
						{...actionProps}
					>
						Edit
					</button>
				)}
			</div>
		</>
	)
}
